import React, { useState, useContext } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/authContext';
import { app } from '../firebase';

const ReminderDiagnosticsPage = () => {
  const [intervalMinutes, setIntervalMinutes] = useState(60);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [error, setError] = useState('');
  const { isSuperAdmin } = useAuth();

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    setError('');
    setReportData([]);
    try {
      const functions = getFunctions(app, 'us-central1');
      const generateDiagnostics = httpsCallable(functions, 'generateFullReminderDiagnostics');
      const result = await generateDiagnostics({ intervalMinutes });
      setReportData(result.data.diagnostics || []);
      setGeneratingReport(false);
    } catch (err) {
      setError('Failed to generate report. ' + (err.message || ''));
      setGeneratingReport(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Reminder System Diagnostics</h1>
        <div className="bg-white p-4 sm:p-6 rounded-lg shadow-md mb-8 flex flex-col sm:flex-row items-center gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 flex-1">
            <label htmlFor="interval" className="text-sm font-medium text-gray-700">Minutes Between Reminders (test):</label>
            <input
              id="interval"
              type="number"
              min={1}
              value={intervalMinutes}
              onChange={e => setIntervalMinutes(Number(e.target.value))}
              className="w-24 p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={handleGenerateReport}
            disabled={generatingReport}
            className="w-full sm:w-auto px-6 py-2 bg-green-600 text-white font-semibold rounded-md shadow-sm hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {generatingReport ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
        {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">{error}</div>}
        {generatingReport && <div className="text-center p-4"><LoadingSpinner /></div>}
        {reportData.length > 0 && (
          <div className="bg-white rounded-lg shadow-md mt-8">
            <h2 className="text-xl font-bold mb-4 p-4 sm:p-6 border-b">Full Reminder Report</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Disciple Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reminder Frequency</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Reminder</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status of Last Reminder</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Next Reminder</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status of Next Reminder</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notification Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Archived</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sent At</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completed At</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completion Reason</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.map((row, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{row.userName}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.discipleName}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.reminderFrequency}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.lastReminder}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.lastReminderStatus}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.nextReminder}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.nextReminderStatus}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.notificationStatus}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          row.archived ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {row.archived ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.createdAt}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.sentAt}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">{row.completedAt}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-700">
                        {row.completionReason && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {row.completionReason.replace(/_/g, ' ')}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReminderDiagnosticsPage; 